'use client'

import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query'
import { useEffect, useRef, type ReactNode } from 'react'
import type { User } from 'firebase/auth'
import { onAuthChange, clearAppToken } from '@/lib/auth'
import { ThemeProvider } from '@/components/providers/ThemeProvider'

function makeQueryClient() {
  // Lazy ref para o client — MutationCache precisa do client mas é
  // construído antes dele.
  let clientRef: QueryClient | null = null

  const mutationCache = new MutationCache({
    // Invalida ['dashboard'] após QUALQUER mutation bem-sucedida.
    // Cobre todos os hooks (existentes e futuros) sem precisar invalidar
    // manualmente em cada onSuccess. O custo é refetch único do dashboard
    // quando o usuário voltar pra ele — irrelevante.
    onSuccess: () => {
      clientRef?.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  const client = new QueryClient({
    mutationCache,
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        retry: 1,
      },
    },
  })
  clientRef = client
  return client
}

let browserQueryClient: QueryClient | undefined

function getQueryClient() {
  if (typeof window === 'undefined') return makeQueryClient()
  if (!browserQueryClient) browserQueryClient = makeQueryClient()
  return browserQueryClient
}

export function Providers({ children }: { children: ReactNode }) {
  const queryClient = getQueryClient()
  // sentinel `undefined` = ainda não recebemos a primeira notificação do Firebase.
  // Após a primeira, guarda o uid (ou null) pra detectar mudanças reais.
  const previousUidRef = useRef<string | null | undefined>(undefined)

  // Sync Firebase Auth token to cookie for middleware
  useEffect(() => {
    const unsubscribe = onAuthChange(async (user: User | null) => {
      const previousUid = previousUidRef.current
      const isFirstCallback = previousUid === undefined
      const userChanged = !isFirstCallback && previousUid !== (user?.uid ?? null)

      // Só limpa cache em LOGIN/LOGOUT real. A primeira notificação é apenas
      // a restauração de sessão do storage no boot — limpar aqui aborta as
      // queries iniciais e trava a UI em "loading" eterno após hard reload.
      if (userChanged) {
        clearAppToken()
        queryClient.clear()
      }

      previousUidRef.current = user?.uid ?? null

      if (user) {
        const token = await user.getIdToken()
        document.cookie = `auth-token=${token}; path=/; max-age=3600`
      } else {
        document.cookie = 'auth-token=; path=/; max-age=0'
      }
    })
    return unsubscribe
  }, [queryClient])

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </ThemeProvider>
  )
}
